import { apiUrl } from './api';

/**
 * Thin wrapper around `fetch` for backend list/detail endpoints.
 *
 * Adds:
 *   - URL building via `apiUrl()` (auth-header injection is handled by the
 *     global fetch monkey-patch in api.ts — out of scope for this helper).
 *   - `AbortSignal` plumbing so callers can cancel in-flight requests on
 *     unmount / dep-change.
 *   - `X-Total-Count` parsing — when the backend exposes it, callers get
 *     the value alongside the parsed body (see backend BE 1).
 *   - Non-2xx → throw with `{ status, message }` so callers can react.
 *
 * Use this for new code touching ContactTable etc. The 215+ existing raw
 * `fetch(apiUrl(...))` calls elsewhere migrate opportunistically.
 */
export interface ApiFetchResult<T> {
  data: T;
  /** Parsed `X-Total-Count` header, or `null` when absent. */
  total: number | null;
  response: Response;
}

export class ApiFetchError extends Error {
  readonly status: number;
  readonly response: Response;
  constructor(status: number, message: string, response: Response) {
    super(message);
    this.name = 'ApiFetchError';
    this.status = status;
    this.response = response;
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit & { params?: Record<string, string | number | boolean> },
): Promise<ApiFetchResult<T>> {
  const url = apiUrl(path, init?.params);
  const { params: _params, ...rest } = init ?? {};
  const response = await fetch(url, rest);
  if (!response.ok) {
    let text = '';
    try {
      text = await response.text();
    } catch {
      /* ignore */
    }
    throw new ApiFetchError(
      response.status,
      `HTTP ${response.status}: ${text || response.statusText}`,
      response,
    );
  }

  // X-Total-Count parsing — accepts both casings just in case.
  const rawTotal =
    response.headers.get('X-Total-Count') ??
    response.headers.get('x-total-count');
  const total = rawTotal !== null ? Number(rawTotal) : null;

  // 204 No Content → empty body; callers shouldn't be doing GET-list against
  // 204 endpoints but guard anyway.
  let data: T;
  if (response.status === 204) {
    data = undefined as unknown as T;
  } else {
    data = (await response.json()) as T;
  }

  return {
    data,
    total: total !== null && !Number.isNaN(total) ? total : null,
    response,
  };
}
